import api from '../../../lib/api';
import type {
  CampaignTestSendRequest,
  CampaignTestSendResult,
  CreateWhatsAppChannelRequest,
  CreateWhatsAppLocalTemplateRequest,
  UpdateWhatsAppChannelRequest,
  UpdateWhatsAppLocalTemplateRequest,
  WhatsAppChannel,
  WhatsAppHealthResult,
  WhatsAppPhoneWhitelistEntry,
  WhatsAppTemplate,
} from '@workspace/types';
import { scopedParams } from '@/features/contact-agent/api/contactAgentScope';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

export type WahaLinkUiStatus = 'SIN_VINCULAR' | 'ESCANEAR_QR' | 'CONECTADO' | 'CAIDO';

export type WahaLinkStatus = {
  uiStatus: WahaLinkUiStatus;
  sessionStatus: string;
  displayPhoneNumber: string;
  linkedAt: string | null;
  lastError: string | null;
  lastCheckedAt: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export const whatsappAdminApi = {
  async getChannelByInstanceBot(instanceBotId: string): Promise<WhatsAppChannel | null> {
    const channels = await this.listChannels();
    return channels.find(channel => channel.instanceBotId === instanceBotId) ?? null;
  },

  async listChannels(tenantId?: string): Promise<WhatsAppChannel[]> {
    const response = await api.get<ApiResponse<WhatsAppChannel[]>>('/admin/whatsapp/channels', {
      params: tenantId ? { tenantId: tenantId.slice(0, 64) } : undefined,
    });
    return requireData(response, 'No se pudieron cargar los canales WhatsApp');
  },

  async createChannel(payload: CreateWhatsAppChannelRequest): Promise<WhatsAppChannel> {
    const response = await api.post<ApiResponse<WhatsAppChannel>>('/admin/whatsapp/channels', payload);
    return requireData(response, 'No se pudo crear el canal WhatsApp');
  },

  async updateChannel(id: string, payload: UpdateWhatsAppChannelRequest): Promise<WhatsAppChannel> {
    const response = await api.patch<ApiResponse<WhatsAppChannel>>(
      `/admin/whatsapp/channels/${id}`,
      payload
    );
    return requireData(response, 'No se pudo actualizar el canal WhatsApp');
  },

  async deleteChannel(id: string): Promise<void> {
    await api.delete(`/admin/whatsapp/channels/${id}`);
  },

  async healthCheck(id: string): Promise<WhatsAppHealthResult> {
    const response = await api.post<ApiResponse<WhatsAppHealthResult>>(
      `/admin/whatsapp/channels/${id}/health-check`
    );
    return requireData(response, 'No se pudo verificar el canal');
  },

  async rotateSecret(id: string): Promise<{ secret: string }> {
    const response = await api.post<ApiResponse<{ secret: string }>>(
      `/admin/whatsapp/channels/${id}/rotate-secret`
    );
    return requireData(response, 'No se pudo rotar el secreto');
  },

  async startWahaLink(channelId: string): Promise<WahaLinkStatus> {
    const response = await api.post<ApiResponse<WahaLinkStatus>>(
      `/admin/whatsapp/channels/${channelId}/waha/start`,
    );
    return requireData(response, 'No se pudo iniciar la vinculación');
  },

  async getWahaStatus(channelId: string): Promise<WahaLinkStatus> {
    const response = await api.get<ApiResponse<WahaLinkStatus>>(
      `/admin/whatsapp/channels/${channelId}/waha/status`,
    );
    return requireData(response, 'No se pudo consultar el estado');
  },

  async getWahaQrBlob(channelId: string): Promise<Blob> {
    const response = await api.get(`/admin/whatsapp/channels/${channelId}/waha/qr`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async logoutWahaLink(channelId: string): Promise<WahaLinkStatus> {
    const response = await api.post<ApiResponse<WahaLinkStatus>>(
      `/admin/whatsapp/channels/${channelId}/waha/logout`,
    );
    return requireData(response, 'No se pudo desvincular');
  },

  async listWhitelist(channelId: string): Promise<WhatsAppPhoneWhitelistEntry[]> {
    const response = await api.get<ApiResponse<WhatsAppPhoneWhitelistEntry[]>>(
      `/admin/whatsapp/channels/${channelId}/whitelist`
    );
    return requireData(response, 'No se pudo cargar la whitelist');
  },

  async addPhone(
    channelId: string,
    phoneE164: string,
    email?: string,
    label?: string
  ): Promise<WhatsAppPhoneWhitelistEntry> {
    const response = await api.post<ApiResponse<WhatsAppPhoneWhitelistEntry>>(
      `/admin/whatsapp/channels/${channelId}/whitelist`,
      { phoneE164, email, label }
    );
    return requireData(response, 'No se pudo agregar el teléfono');
  },

  async removePhone(channelId: string, phone: string): Promise<void> {
    await api.delete(`/admin/whatsapp/channels/${channelId}/whitelist/${encodeURIComponent(phone)}`);
  },

  async listTemplates(channelId: string): Promise<WhatsAppTemplate[]> {
    const response = await api.get<ApiResponse<WhatsAppTemplate[]>>(
      `/admin/whatsapp/channels/${channelId}/templates`
    );
    return requireData(response, 'No se pudieron cargar las plantillas');
  },

  async createLocalTemplate(
    channelId: string,
    payload: CreateWhatsAppLocalTemplateRequest,
  ): Promise<WhatsAppTemplate> {
    const response = await api.post<ApiResponse<WhatsAppTemplate>>(
      `/admin/whatsapp/channels/${channelId}/templates/local`,
      payload,
    );
    return requireData(response, 'No se pudo crear la plantilla');
  },

  async updateLocalTemplate(
    channelId: string,
    templateId: string,
    payload: UpdateWhatsAppLocalTemplateRequest,
  ): Promise<WhatsAppTemplate> {
    const response = await api.patch<ApiResponse<WhatsAppTemplate>>(
      `/admin/whatsapp/channels/${channelId}/templates/local/${templateId}`,
      payload,
    );
    return requireData(response, 'No se pudo actualizar la plantilla');
  },

  async deleteLocalTemplate(channelId: string, templateId: string): Promise<void> {
    await api.delete(`/admin/whatsapp/channels/${channelId}/templates/local/${templateId}`);
  },

  async syncTemplates(channelId: string): Promise<{ synced: number }> {
    const response = await api.post<ApiResponse<{ synced: number }>>(
      `/admin/whatsapp/channels/${channelId}/templates/sync`
    );
    return requireData(response, 'No se pudieron sincronizar las plantillas');
  },

  async testSend(
    channelId: string,
    payload: CampaignTestSendRequest,
    tenantId?: string,
  ): Promise<CampaignTestSendResult> {
    const response = await api.post<ApiResponse<CampaignTestSendResult>>(
      `/admin/whatsapp/channels/${channelId.slice(0, 64)}/test-send`,
      payload,
      { params: scopedParams(tenantId ? { tenantId } : undefined) },
    );
    return requireData(response, 'No se pudo enviar la prueba');
  },
};
